<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <AssetFilters title="Projets" asset-label="Projets" :active-filter="viewFilter" :stats="stats"
      :is-loading="isLoading" @update:active-filter="setFilter" @refresh="refreshProjects" />

    <ProjectSubset :projects="filteredProjects" :show-toolbar="true" :show-actions="true" :show-edit-button="true"
      :show-delete-button="true" @create="openCreationModal" @edit="editProject" @delete="handleDeleteProject"
      @view="showProject" />

    <ProjectsCreator v-model:show="showCreatorModal" :user-id="userId" :mode="creatorMode"
      :initial-data="selectedProject" @project-created="handleProjectCreated" @project-updated="handleProjectUpdated" />
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { useProjects } from '@/composables/useAssets';
import { useAuthStore } from '@/stores/auth';
import type { NotificationType } from "@/types/notification";
import { computed, ref } from "vue";
import { useRouter } from 'vue-router';
import { projects } from "../../api/projects";
import type { Project } from "../../api/types/entities/project/types";
import Breadcrumbs from "../../components/Breadcrumbs.vue";
import Notification from "../../components/Notification.vue";
import AssetFilters from "../../components/UI/AssetFilters.vue";
import ProjectsCreator from "../../components/creators/ProjectsCreator.vue";
import ProjectSubset from "../../components/subsets/ProjectSubset.vue";
import { useBreadcrumbsStore } from "../../stores/breadcrumbs";

// Stores et composables
const router = useRouter();
const authStore = useAuthStore();
const breadcrumbsStore = useBreadcrumbsStore();
const {
  filteredAssets: filteredProjects,
  isLoading,
  stats,
  viewFilter,
  setFilter,
  refreshAssets: refreshProjects,
  addAsset: addProject,
  updateAsset: updateProject,
  removeAsset: removeProject
} = useProjects({ autoLoad: true });

// État local
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showCreatorModal = ref(false);
const creatorMode = ref<'create' | 'edit'>('create');
const selectedProject = ref<Project | undefined>(undefined);

// Computed
const userId = computed((): string => authStore.user?.uuid || '');

// Initialiser les breadcrumbs

const crumbs = [
  { name: 'Outils', path: '/dashboard' },
  { name: 'Projets', path: '/myprojects' }
];

breadcrumbsStore.updateBreadcrumbs(crumbs);

const showProject = (project: Project): void => {
  router.push(`/projects/${project.uuid}`);
};

const editProject = (project: Project): void => {
  // Vérifier si l'utilisateur peut éditer ce projet (pas d'édition sur projets publics d'autres utilisateurs)
  if (!authStore.canEditAsset(project as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas éditer ce projet", "error");
    return;
  }

  selectedProject.value = project;
  creatorMode.value = 'edit';
  showCreatorModal.value = true;
};

const handleDeleteProject = async (uuid: string): Promise<void> => {
  // Trouver le projet pour vérifier les permissions
  const project = filteredProjects.value.find(p => p.uuid === uuid);
  if (!project) {
    showNotification("Projet non trouvé", "error");
    return;
  }

  // Vérifier si l'utilisateur peut supprimer ce projet (pas de suppression sur projets publics d'autres utilisateurs)
  if (!authStore.canDeleteAsset(project as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas supprimer ce projet", "error");
    return;
  }

  try {
    await projects.deleteProject(uuid);
    removeProject(uuid);
    showNotification("Projet supprimé avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression du projet:", error);
    showNotification("Erreur lors de la suppression du projet", "error");
  }
};

const openCreationModal = (): void => {
  selectedProject.value = undefined;
  creatorMode.value = 'create';
  showCreatorModal.value = true;
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};

const handleProjectCreated = (project: Project): void => {
  console.log('[MyProjects] handleProjectCreated appelé avec:', project);
  addProject(project);
  console.log('[MyProjects] addProject appelé');
  showNotification("Projet créé avec succès", "success");
};

const handleProjectUpdated = (project: Project): void => {
  updateProject(project);
  showNotification("Projet mis à jour avec succès", "success");
};
</script>

<style>
/* Les styles sont gérés par les composants enfants */
</style>